import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Calculator, TrendingUp, Info, Download, Share2, BookOpen } from 'lucide-react';
import { Badge } from '../ui/badge';
import jsPDF from 'jspdf';

const FreelanceRevenueSimulator = () => {
  const [inputs, setInputs] = useState({
    tjm: '400',
    joursParMois: '18',
    joursParAn: '198',
    moisTravailles: '11',
    statut: 'micro-entreprise'
  });

  const [results, setResults] = useState({});

  // TVA fixée à 20% pour SASU/EURL, 0% pour micro-entreprise
  const TAUX_TVA = 20;

  const calculateRevenue = () => {
    const tjm = parseFloat(inputs.tjm) || 0;
    const joursParAn = parseFloat(inputs.joursParAn) || 0;
    const statut = inputs.statut;
    
    const chiffreAffairesAnnuel = tjm * joursParAn;
    const chiffreAffairesMensuel = chiffreAffairesAnnuel / 12;
    
    let chargesSociales = 0;
    let impotsSurRevenu = 0;
    let cotisationsRetraite = 0;
    let tvaCollectee = 0;
    
    // Calculs selon le statut
    switch (statut) {
      case 'micro-entreprise':
        // Micro-entreprise : pas de TVA (franchise en base)
        chargesSociales = chiffreAffairesAnnuel * 0.22; // 22% pour prestations de services
        impotsSurRevenu = chiffreAffairesAnnuel * 0.022; // 2.2% versement libératoire
        tvaCollectee = 0; // Pas de TVA en micro-entreprise
        break;
        
      case 'sasu':
        chargesSociales = chiffreAffairesAnnuel * 0.45; // ~45% charges patronales + salariales
        const baseImposable = chiffreAffairesAnnuel - chargesSociales;
        if (baseImposable > 10777) {
          impotsSurRevenu = (baseImposable - 10777) * 0.11;
        }
        cotisationsRetraite = chiffreAffairesAnnuel * 0.0775;
        tvaCollectee = chiffreAffairesAnnuel * (TAUX_TVA / 100);
        break;
        
      case 'eurl':
        chargesSociales = chiffreAffairesAnnuel * 0.30; // ~30% charges sociales TNS
        const baseImposableEURL = chiffreAffairesAnnuel - chargesSociales;
        if (baseImposableEURL > 10777) {
          impotsSurRevenu = (baseImposableEURL - 10777) * 0.11;
        }
        cotisationsRetraite = chiffreAffairesAnnuel * 0.0775;
        tvaCollectee = chiffreAffairesAnnuel * (TAUX_TVA / 100);
        break;
        
      default:
        break;
    }
    
    const totalCharges = chargesSociales + impotsSurRevenu + cotisationsRetraite;
    const revenuNetAnnuel = chiffreAffairesAnnuel - totalCharges;
    const revenuNetMensuel = revenuNetAnnuel / 12;
    const tauxCharges = chiffreAffairesAnnuel > 0 ? (totalCharges / chiffreAffairesAnnuel) * 100 : 0;
    
    const chiffreAffairesHT = chiffreAffairesAnnuel;
    const chiffreAffairesTTC = chiffreAffairesAnnuel + tvaCollectee;
    
    setResults({
      chiffreAffairesHT,
      chiffreAffairesTTC,
      chiffreAffairesMensuel,
      chargesSociales,
      impotsSurRevenu,
      cotisationsRetraite,
      totalCharges,
      revenuNetAnnuel,
      revenuNetMensuel,
      tauxCharges,
      tvaCollectee
    });
  };

  useEffect(() => {
    calculateRevenue();
  }, [inputs]);

  const handleInputChange = (field, value) => {
    setInputs(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Synchroniser jours/mois et jours/an
  const handleJoursParMoisChange = (value) => {
    const joursParMois = parseFloat(value) || 0;
    const joursParAn = (joursParMois * 12).toFixed(1).replace(/\.0$/, '');
    setInputs(prev => ({
      ...prev,
      joursParMois: value,
      joursParAn: joursParAn
    }));
  };

  const handleJoursParAnChange = (value) => {
    const joursParAn = parseFloat(value) || 0;
    const joursParMois = (joursParAn / 12).toFixed(1).replace(/\.0$/, '');
    setInputs(prev => ({
      ...prev,
      joursParAn: value,
      joursParMois: joursParMois
    }));
  };

  const handleStatutChange = (value) => {
    setInputs(prev => ({
      ...prev,
      statut: value
    }));
  };

  const shareLink = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({
        title: 'Simulateur Revenus Freelance - Bozzam',
        text: 'Calculez votre revenu net selon votre statut juridique',
        url: url
      });
    } else {
      navigator.clipboard.writeText(url);
      alert('Lien copié dans le presse-papier !');
    }
  };

  const exportResults = () => {
    const doc = new jsPDF();
    doc.setFillColor(30, 64, 175); doc.rect(0, 0, 210, 40, 'F');
    doc.setTextColor(255, 255, 255); doc.setFontSize(20);
    doc.text('Bozzam - Revenus Freelance', 15, 25);
    doc.setFontSize(10); doc.text('Simulation réalisée sur blog.bozzam.com', 15, 35);
    doc.setTextColor(0, 0, 0); let y = 55;
    doc.setFontSize(14); doc.text('Paramètres', 15, y); y += 10;
    doc.setFontSize(11);
    doc.text(`TJM : ${inputs.tjm} EUR`, 15, y); y += 7;
    doc.text(`Jours/mois : ${inputs.joursParMois} | Jours/an : ${inputs.joursParAn}`, 15, y); y += 7;
    doc.text(`Statut : ${inputs.statut}`, 15, y); y += 15;
    doc.setFontSize(14); doc.text('Résultats', 15, y); y += 10;
    doc.setFontSize(11);
    [
      [`CA HT annuel`, `${results.chiffreAffairesHT?.toLocaleString()} EUR`],
      [`CA TTC annuel`, `${results.chiffreAffairesTTC?.toLocaleString()} EUR`],
      [`Charges sociales`, `${results.chargesSociales?.toLocaleString()} EUR`],
      [`Impôts sur le revenu`, `${results.impotsSurRevenu?.toLocaleString()} EUR`],
      [`Total charges`, `${results.totalCharges?.toLocaleString()} EUR`],
      [`Revenu net annuel`, `${results.revenuNetAnnuel?.toLocaleString()} EUR`],
      [`Revenu net mensuel`, `${results.revenuNetMensuel?.toLocaleString()} EUR`],
      [`Taux de charges`, `${results.tauxCharges?.toFixed(1)}%`],
    ].forEach(([l, v]) => { doc.text(`${l} : ${v}`, 15, y); y += 7; });
    y += 10;
    doc.setFontSize(9); doc.setTextColor(100, 100, 100);
    doc.text('Sources : URSSAF 2026, DGFiP, INSEE. Barèmes officiels 2026.', 15, y);
    doc.text('Consultez un expert-comptable pour votre situation personnelle.', 15, y + 5);
    doc.save('simulation-revenus-freelance-bozzam.pdf');
  };

  const isMicroEntreprise = inputs.statut === 'micro-entreprise';

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Formulaire */}
        <Card className="h-fit">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calculator className="h-5 w-5 text-blue-600" />
              Simulateur Revenus Freelance
            </CardTitle>
            <CardDescription>
              Calculez votre revenu net selon votre statut juridique
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-6">
            <div>
              <Label htmlFor="tjm">TJM (Taux Journalier Moyen) en €</Label>
              <Input
                id="tjm"
                type="text"
                inputMode="numeric"
                value={inputs.tjm}
                onChange={(e) => handleInputChange('tjm', e.target.value.replace(/[^0-9.]/g, ''))}
                className="mt-1"
                placeholder="400"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="joursParMois">Jours travaillés / mois</Label>
                <Input
                  id="joursParMois"
                  type="text"
                  inputMode="decimal"
                  value={inputs.joursParMois}
                  onChange={(e) => handleJoursParMoisChange(e.target.value.replace(/[^0-9.]/g, ''))}
                  className="mt-1"
                  placeholder="18"
                />
              </div>
              
              <div>
                <Label htmlFor="joursParAn">Jours travaillés / an</Label>
                <Input
                  id="joursParAn"
                  type="text"
                  inputMode="decimal"
                  value={inputs.joursParAn}
                  onChange={(e) => handleJoursParAnChange(e.target.value.replace(/[^0-9.]/g, ''))}
                  className="mt-1"
                  placeholder="198"
                />
              </div>
            </div>
            
            <p className="text-xs text-gray-500 -mt-4">
              Les deux champs sont synchronisés automatiquement
            </p>
            
            <div>
              <Label htmlFor="tva" className="text-gray-400">
                Taux TVA (%)
                {isMicroEntreprise ? (
                  <span className="block text-xs text-gray-400">Non applicable</span>
                ) : (
                  <span className="block text-xs text-gray-400">Taux légal fixe</span>
                )}
              </Label>
              <Input
                id="tva"
                type="text"
                value={isMicroEntreprise ? 'N/A' : '20'}
                className="mt-1 bg-gray-100 text-gray-500 cursor-not-allowed"
                disabled={true}
                readOnly
              />
            </div>
            
            <div>
              <Label htmlFor="statut">Statut juridique</Label>
              <Select 
                value={inputs.statut} 
                onValueChange={handleStatutChange}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="micro-entreprise">Micro-entreprise</SelectItem>
                  <SelectItem value="sasu">SASU à l'IS</SelectItem>
                  <SelectItem value="eurl">EURL à l'IS</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {!isMicroEntreprise && (
              <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                <div className="flex items-start gap-2">
                  <Info className="h-4 w-4 text-purple-600 mt-0.5" />
                  <div className="text-sm text-purple-800">
                    <p className="font-medium mb-1">Vous souhaitez simuler à l'IR ?</p>
                    <p>Pour simuler une SASU ou EURL à l'impôt sur le revenu (IR), rendez-vous sur{' '}
                      <a 
                        href="https://app.bozzam.com" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="font-semibold underline hover:text-purple-900"
                      >
                        l'application Bozzam
                      </a>.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {isMicroEntreprise && (
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-start gap-2">
                  <Info className="h-4 w-4 text-amber-600 mt-0.5" />
                  <div className="text-sm text-amber-800">
                    <p className="font-medium mb-1">Franchise en base de TVA</p>
                    <p>En micro-entreprise, vous bénéficiez de la franchise en base de TVA. Vous ne facturez pas de TVA à vos clients.</p>
                  </div>
                </div>
              </div>
            )}
            
            <div className="p-4 bg-blue-50 rounded-lg">
              <div className="flex items-start gap-2">
                <Info className="h-4 w-4 text-blue-600 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">Sources officielles 2026</p>
                  <p>Taux URSSAF, barèmes DGFiP et dernières réformes votées. Données certifiées INSEE.</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Résultats */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-green-600" />
              Résultats de votre simulation
            </CardTitle>
            <div className="flex gap-2 flex-wrap">
              <Badge variant="secondary">{inputs.statut}</Badge>
              <Badge className="bg-green-100 text-green-800">
                Taux de charges: {results.tauxCharges?.toFixed(1)}%
              </Badge>
              {!isMicroEntreprise && (
                <Badge className="bg-blue-100 text-blue-800">
                  TVA: 20%
                </Badge>
              )}
            </div>
          </CardHeader>
          
          <CardContent className="space-y-6">
            {/* Chiffre d'affaires */}
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-blue-50 rounded-lg">
                <p className="text-sm font-medium text-blue-600 mb-1">CA HT Annuel</p>
                <p className="text-2xl font-bold text-blue-900">
                  {results.chiffreAffairesHT?.toLocaleString()}€
                </p>
              </div>
              <div className={`p-4 rounded-lg ${isMicroEntreprise ? 'bg-gray-50' : 'bg-blue-50'}`}>
                <p className={`text-sm font-medium mb-1 ${isMicroEntreprise ? 'text-gray-500' : 'text-blue-600'}`}>
                  CA TTC Annuel
                  {isMicroEntreprise && <span className="block text-xs">(= CA HT)</span>}
                </p>
                <p className={`text-2xl font-bold ${isMicroEntreprise ? 'text-gray-600' : 'text-blue-900'}`}>
                  {results.chiffreAffairesTTC?.toLocaleString()}€
                </p>
              </div>
            </div>

            {/* TVA collectée (seulement pour SASU/EURL) */}
            {!isMicroEntreprise && results.tvaCollectee > 0 && (
              <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-purple-700">TVA collectée (à reverser)</span>
                  <span className="text-sm font-bold text-purple-900">
                    {results.tvaCollectee?.toLocaleString()}€
                  </span>
                </div>
              </div>
            )}
            
            {/* Charges détaillées */}
            <div className="space-y-3">
              <h4 className="font-semibold text-gray-900">Détail des charges</h4>
              <div className="space-y-2">
                <div className="flex justify-between items-center p-3 bg-red-50 rounded">
                  <span className="text-sm font-medium text-red-700">Charges sociales</span>
                  <span className="text-sm font-bold text-red-900">
                    {results.chargesSociales?.toLocaleString()}€
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-red-50 rounded">
                  <span className="text-sm font-medium text-red-700">Impôts sur le revenu</span>
                  <span className="text-sm font-bold text-red-900">
                    {results.impotsSurRevenu?.toLocaleString()}€
                  </span>
                </div>
                {results.cotisationsRetraite > 0 && (
                  <div className="flex justify-between items-center p-3 bg-red-50 rounded">
                    <span className="text-sm font-medium text-red-700">Cotisations retraite</span>
                    <span className="text-sm font-bold text-red-900">
                      {results.cotisationsRetraite?.toLocaleString()}€
                    </span>
                  </div>
                )}
                <div className="flex justify-between items-center p-3 bg-red-100 rounded border-2 border-red-200">
                  <span className="text-sm font-bold text-red-800">Total charges</span>
                  <span className="text-sm font-bold text-red-900">
                    {results.totalCharges?.toLocaleString()}€
                  </span>
                </div>
              </div>
            </div>
            
            {/* Revenus nets */}
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-green-50 rounded-lg border-2 border-green-200">
                <p className="text-sm font-medium text-green-600 mb-1">Revenu Net Annuel</p>
                <p className="text-2xl font-bold text-green-900">
                  {results.revenuNetAnnuel?.toLocaleString()}€
                </p>
              </div>
              <div className="p-4 bg-green-50 rounded-lg border-2 border-green-200">
                <p className="text-sm font-medium text-green-600 mb-1">Revenu Net Mensuel</p>
                <p className="text-2xl font-bold text-green-900">
                  {results.revenuNetMensuel?.toLocaleString()}€
                </p>
              </div>
            </div>
            
            {/* Actions */}
            <div className="grid grid-cols-3 gap-3">
              <Button onClick={shareLink} variant="outline">
                <Share2 className="h-4 w-4 mr-2" />
                Partager
              </Button>
              
              <Button onClick={exportResults} variant="outline">
                <Download className="h-4 w-4 mr-2" />
                Exporter
              </Button>
              
              <Button 
                className="bg-blue-600 hover:bg-blue-700"
                onClick={() => window.open(process.env.REACT_APP_BOZZAM_APP_URL, '_blank')}
              >
                Bozzam App
              </Button>
            </div>
            
            <div className="text-xs text-gray-500 text-center pt-2 border-t">
              <p>Simulation indicative - Sources : URSSAF, DGFiP, INSEE</p>
              <p>Barèmes officiels 2026 • Consultez un expert-comptable pour votre situation</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default FreelanceRevenueSimulator;
